import React, { useState, useEffect } from 'react';
import { getSnapshots, createSnapshot, deleteSnapshot } from '../lib/sessionSnapshotter';
import { playClick, playSuccess } from '../lib/soundEffects';

export default function SessionSnapshotModal({
  isOpen,
  onClose,
  bookmarks = [],
  folders = [],
  onRestoreSnapshot,
}) {
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotName, setSnapshotName] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSnapshots(getSnapshots());
      setSuccessMsg('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        playClick();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleCapture = (e) => {
    e.preventDefault();
    playClick();
    const created = createSnapshot(snapshotName.trim(), bookmarks, folders);
    setSnapshots(getSnapshots());
    setSnapshotName('');
    setSuccessMsg(`✓ Created "${created.name}" with ${bookmarks.length} links!`);
    playSuccess();
  };

  const handleDelete = (id) => {
    playClick();
    const updated = deleteSnapshot(id);
    setSnapshots(updated);
  };

  const handleRestore = (snap) => {
    playClick();
    if (confirm(`Restore snapshot "${snap.name}" with ${snap.itemCount} items?`)) {
      if (onRestoreSnapshot) {
        onRestoreSnapshot(snap);
      }
      playSuccess();
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '680px',
          maxHeight: '88vh',
          backgroundColor: '#0a0f16',
          border: '1px solid rgba(56, 189, 248, 0.35)',
          borderRadius: '16px',
          padding: '1.75rem',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.9), 0 0 35px rgba(56, 189, 248, 0.15)',
          color: '#e2e8f0',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '1.75rem' }}>📸</span>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#38bdf8', letterSpacing: '0.05em' }}>
                WORKSPACE TAB SESSION SNAPSHOTTER
              </h2>
              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                Serialize and restore multi-window browser sessions & dated collections
              </div>
            </div>
          </div>
          <button
            onClick={() => { playClick(); onClose(); }}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#94a3b8',
              fontSize: '1.25rem',
              cursor: 'pointer',
              padding: '0.4rem',
            }}
          >
            ✕
          </button>
        </div>

        {/* Capture Form */}
        <form
          onSubmit={handleCapture}
          style={{
            display: 'flex',
            gap: '0.5rem',
            padding: '1rem',
            borderRadius: '10px',
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            marginBottom: '1.25rem',
          }}
        >
          <input
            type="text"
            placeholder="Snapshot Label (e.g. AI Research Sprint, Evening Reading)..."
            value={snapshotName}
            onChange={(e) => setSnapshotName(e.target.value)}
            style={{
              flex: 1,
              padding: '0.6rem 0.8rem',
              borderRadius: '6px',
              border: '1px solid rgba(56, 189, 248, 0.3)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: '#fff',
              fontSize: '0.85rem',
            }}
          />
          <button
            type="submit"
            style={{
              padding: '0.6rem 1.25rem',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: '#38bdf8',
              color: '#000',
              fontWeight: 700,
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            📸 Capture Now ({bookmarks.length} links)
          </button>
        </form>

        {successMsg && (
          <div style={{ marginBottom: '1rem', padding: '0.5rem 0.75rem', borderRadius: '6px', backgroundColor: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.4)', color: '#34d399', fontSize: '0.8rem' }}>
            {successMsg}
          </div>
        )}

        {/* Snapshots List */}
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {snapshots.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2.5rem', color: '#64748b', fontSize: '0.9rem' }}>
              No snapshots captured yet. Click "Capture Now" to freeze active workspace!
            </div>
          ) : (
            snapshots.map((snap) => (
              <div
                key={snap.id}
                style={{
                  padding: '0.9rem 1rem',
                  borderRadius: '10px',
                  backgroundColor: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#f1f5f9' }}>
                    {snap.name}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.2rem' }}>
                    {new Date(snap.timestamp).toLocaleString()} • {snap.itemCount} Links • {snap.folderCount} Folders
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleRestore(snap)}
                    style={{
                      padding: '0.4rem 0.8rem',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: '#10b981',
                      color: '#000',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                    }}
                  >
                    ⚡ Restore
                  </button>
                  <button
                    onClick={() => handleDelete(snap.id)}
                    style={{
                      padding: '0.4rem 0.6rem',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      backgroundColor: 'transparent',
                      color: '#f87171',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                    }}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
